import React, { useState, useEffect, useRef } from 'react';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import TokenIcon from './TokenIcon';

// Load Stripe outside of a component's render to avoid
// recreating the Stripe object on every render.
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const formatPrice = (price, suffix = '') => {
  if (!price) return 'N/A';
  const amount = new Intl.NumberFormat('en-US', { style: 'currency', currency: price.currency }).format(price.amount / 100);
  return `${amount}${suffix}`;
};

// Sliding segmented control: the gold thumb translates between the two halves,
// so only the selected purchase type's packages are ever on screen.
const PurchaseTypeSlider = ({ value, onChange, disabled }) => {
  const options = [
    { value: 'one-time', label: 'One-time' },
    { value: 'subscription', label: 'Subscription' },
  ];

  return (
    <div role="tablist" aria-label="Purchase type" className="relative flex bg-wp-surface rounded-full p-1 mt-6">
      <span
        aria-hidden="true"
        className={`absolute top-1 bottom-1 left-1 w-[calc(50%-0.25rem)] rounded-full bg-wp-accent transition-transform duration-200 ease-out ${
          value === 'subscription' ? 'translate-x-full' : 'translate-x-0'
        }`}
      />
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            role="tab"
            type="button"
            aria-selected={isActive}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={`relative z-10 flex-1 rounded-full py-2 text-sm font-bold transition-colors disabled:opacity-50 ${
              isActive ? 'text-wp-bg' : 'text-wp-muted hover:text-white'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

const ProductRow = ({ icon, title, subtitle, buttonLabel, selected, disabled, onSelect }) => (
  <div className={`flex items-center bg-wp-surface rounded-xl p-4 gap-4 ${selected ? 'ring-2 ring-wp-accent' : ''}`}>
    <div className="flex-1 space-y-1">
      <p className="flex items-center gap-1.5 text-white text-[15px] font-semibold">{icon} {title}</p>
      <p className="text-wp-muted text-[13px]">{subtitle}</p>
    </div>
    <button
      onClick={onSelect}
      disabled={disabled}
      className="min-w-[110px] bg-wp-accent text-wp-bg text-sm font-bold rounded-lg px-4 py-2.5 hover:opacity-80 transition-opacity disabled:opacity-50"
    >
      {buttonLabel}
    </button>
  </div>
);

const TokenPurchasePage = () => {
  const { user } = useAuth();
  const [oneTimeProducts, setOneTimeProducts] = useState([]);
  const [subscriptionProducts, setSubscriptionProducts] = useState([]);
  const [selectedPriceId, setSelectedPriceId] = useState(null);
  // Drives the slider, the list on screen, the endpoint, and the checkout heading —
  // a purchase can only ever be started from the type currently selected.
  const [purchaseType, setPurchaseType] = useState('one-time');
  const [clientSecret, setClientSecret] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingProducts, setIsFetchingProducts] = useState(true);
  const [error, setError] = useState('');
  const checkoutRef = useRef(null);

  // Fetch products from the backend when the component mounts
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsFetchingProducts(true);
        setError('');
        const [oneTimeRes, subRes] = await Promise.all([
          api.get('/payments/products'),
          api.get('/payments/subscriptions/products'),
        ]);
        setOneTimeProducts(oneTimeRes.data);
        setSubscriptionProducts(subRes.data);
      } catch (err) {
        console.error('Failed to fetch token packages:', err);
        setError('Could not load available token packages. Please try again later.');
      } finally {
        setIsFetchingProducts(false);
      }
    };

    fetchProducts();
  }, []);

  // The slider is hidden when one side has no products, so fall back to whichever
  // side actually has them rather than rendering an empty panel.
  const activeType = oneTimeProducts.length === 0
    ? 'subscription'
    : subscriptionProducts.length === 0
      ? 'one-time'
      : purchaseType;

  // Flipping the slider abandons any checkout in progress — an embedded Checkout
  // session belongs to the type that opened it.
  const handleTypeChange = (nextType) => {
    if (nextType === purchaseType) return;
    setPurchaseType(nextType);
    setSelectedPriceId(null);
    setClientSecret('');
    setError('');
  };

  const handleSelect = async (product) => {
    setIsLoading(true);
    setError('');
    setClientSecret('');
    setSelectedPriceId(product.price.id);

    try {
      const endpoint = activeType === 'subscription'
        ? '/payments/create-checkout-session'
        : '/payments/create-payment-intent'; // Returns an embedded Checkout session client secret

      const response = await api.post(endpoint, { priceId: product.price.id });
      setClientSecret(response.data.clientSecret);
      // Bring the embedded checkout into view once it renders.
      setTimeout(() => checkoutRef.current?.scrollIntoView({ behavior: 'smooth' }), 300);
    } catch (err) {
      console.error('Error initiating purchase:', err);
      setError(err.response?.data?.message || 'Could not initiate payment. Please select your option again.');
      setSelectedPriceId(null);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-wp-surface rounded-2xl p-6 mb-2">
        <p className="text-wp-muted text-[11px] tracking-[3px]">CURRENT BALANCE</p>
        <div className="flex items-center gap-3 mt-2">
          <TokenIcon size={40} flat={false} />
          <span className="text-wp-accent text-5xl font-bold">
            {user ? (user.wallpassTokenBalance ?? 0).toLocaleString() : '—'}
          </span>
        </div>
        <p className="text-wp-muted text-[13px] mt-1">Available tokens</p>
      </div>

      {isFetchingProducts ? (
        <p className="text-center text-wp-muted py-10">Loading packages…</p>
      ) : (
        <>
          {/* Only offer the slider when there is something on both sides of it. */}
          {oneTimeProducts.length > 0 && subscriptionProducts.length > 0 && (
            <PurchaseTypeSlider value={activeType} onChange={handleTypeChange} disabled={isLoading} />
          )}

          <div role="tabpanel">
            {activeType === 'subscription' ? (
              subscriptionProducts.length > 0 && (
                <>
                  <h2 className="text-white text-lg font-bold mt-6">Unlimited Access</h2>
                  <p className="text-wp-muted text-[13px] mb-3 mt-0.5">Monthly subscription — tokens renew automatically</p>
                  <div className="space-y-2">
                    {subscriptionProducts.map((product) => (
                      <ProductRow
                        key={product.price.id}
                        icon="👑"
                        title={`${product.name} / ${product.price.interval}`}
                        subtitle={`${Number(product.tokens).toLocaleString()} tokens every ${product.price.interval}`}
                        buttonLabel={formatPrice(product.price, `/${product.price.interval}`)}
                        selected={selectedPriceId === product.price.id}
                        disabled={isLoading}
                        onSelect={() => handleSelect(product)}
                      />
                    ))}
                  </div>
                </>
              )
            ) : (
              oneTimeProducts.length > 0 && (
                <>
                  <h2 className="text-white text-lg font-bold mt-6">Refill Tokens</h2>
                  <p className="text-wp-muted text-[13px] mb-3 mt-0.5">One-time purchase</p>
                  <div className="space-y-2">
                    {oneTimeProducts.map((product) => (
                      <ProductRow
                        key={product.price.id}
                        icon={<TokenIcon size={16} />}
                        title={product.name}
                        subtitle={`${Number(product.tokens).toLocaleString()} tokens`}
                        buttonLabel={formatPrice(product.price)}
                        selected={selectedPriceId === product.price.id}
                        disabled={isLoading}
                        onSelect={() => handleSelect(product)}
                      />
                    ))}
                  </div>
                </>
              )
            )}
          </div>
        </>
      )}

      {isLoading && <p className="text-center text-wp-muted mt-6">Initializing payment…</p>}
      {error && <p className="text-center text-wp-danger mt-6">{error}</p>}

      {clientSecret && (
        <div ref={checkoutRef} className="mt-8 pt-6 border-t border-wp-border">
          <h3 className="text-white text-lg font-bold text-center mb-4">
            {activeType === 'subscription' ? 'Complete Your Subscription' : 'Complete Your Purchase'}
          </h3>
          <div className="rounded-2xl overflow-hidden bg-white">
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
              <EmbeddedCheckout className="w-full" />
            </EmbeddedCheckoutProvider>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenPurchasePage;
